import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PunchupStackedBarChart } from 'features/common/components/Punchup/primitives/charts/PunchupStackedBarChart';
import { PunchupChartTooltip } from 'features/common/components/Punchup/primitives/charts/PunchupChartTooltip';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { IdParamsType } from 'utils/types';
import { useHighlights } from 'features/common/hooks/useHighlights';
import { formatCompactNumber } from 'features/common/utils/chartFormatters';

// Mon-first business-week order. DAY_LABELS is the display order; DAY_ORDER maps each display
// index to its JS getDay() value (0=Sun) so the bars read Mon→Sun.
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface PunchupDayOfWeekSectionProps {
	ticketTypeColors: Record<string, string>;
}

const PunchupDayOfWeekSection: FC<PunchupDayOfWeekSectionProps> = ({ ticketTypeColors }) => {
	const { t } = useTranslation();
	const { id } = useParams<IdParamsType>();
	const { eventHighlights, isLoading } = useHighlights(id);

	// Aggregate paid sales by UTC day-of-week and ticket type, then project the chart's data/bars/total.
	// Memoised so the reduce doesn't rerun — and the arrays keep a stable reference for the React.memo'd
	// PunchupStackedBarChart — on every parent render. Date parsing uses new Date(...) + getUTCDay(), never
	// split('-') (which yields NaN on an ISO string with a time component and silently zeros every bar).
	const { data, bars, totalSold } = useMemo(() => {
		const typeNames = Object.keys(ticketTypeColors);
		const salesByDayAndType: Record<string, number[]> = Object.fromEntries(
			typeNames.map((name) => [name, new Array(7).fill(0)])
		);
		for (const sale of eventHighlights?.sales ?? []) {
			const d = new Date(sale.date as string);
			const dayIndex = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).getUTCDay();
			for (const tt of sale.ticket_types) {
				if (salesByDayAndType[tt.name]) {
					salesByDayAndType[tt.name][dayIndex] += tt.sold;
				}
			}
		}
		return {
			data: DAY_ORDER.map((jsDay, i) => {
				const entry: Record<string, unknown> = { name: DAY_LABELS[i] };
				for (const name of typeNames) {
					entry[name] = salesByDayAndType[name][jsDay];
				}
				return entry;
			}),
			bars: typeNames.map((name) => ({ dataKey: name, color: ticketTypeColors[name], name })),
			totalSold: typeNames.reduce((total, name) => total + salesByDayAndType[name].reduce((s, v) => s + v, 0), 0),
		};
	}, [eventHighlights?.sales, ticketTypeColors]);

	if (!isLoading && !eventHighlights?.sales.length) return null;

	return (
		<PunchupStackedBarChart
			isLoading={isLoading}
			data={data}
			bars={bars}
			xAxisKey="name"
			title={t('analytics-day-of-week')}
			totalValue={totalSold}
			yAxisFormatter={formatCompactNumber}
			customTooltip={PunchupChartTooltip}
			helpButton={
				<PunchupHelpButton
					mainText="Total paid tickets sold aggregated by day of week across the entire on-sale window, broken down by ticket type. Comped tickets are not included."
					codeText={'covers the full on-sale window — no date range controls for this chart'}
					secondaryText="If a large share of sales consistently lands on Thursday, that's when marketing has the highest impact — plan campaigns for the night before."
				/>
			}
		/>
	);
};

export default PunchupDayOfWeekSection;
